import React, { useCallback } from 'react'
import { CellProps, useTable } from 'react-table'
import { SeedCrop } from 'modules/domain/seedCrop/types'
import SeedCropSelectors from 'modules/domain/seedCrop/selectors'
import SeedCropActions from 'modules/domain/seedCrop/duck'
import { useSeedCropList } from 'modules/domain/seedCrop/hooks'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath, useHistory } from 'react-router-dom'
import Routes from '../routes'
import * as TComponents from 'views/components/CommonTableComponents/CommonTableComponents'
import { Progress } from 'modules/types'
import useDownload from 'hooks/useDownload'
import { endpoints } from 'modules/endpoints'
import {
  AdvancedHeadCell,
  Table,
  TableHead,
  TableHeadRow,
  TableBody,
  TableBodyRow,
  TableBodyCell,
  TableNoData,
  useAction,
  usePersistentScroll,
  Button,
} from '@agro-club/frontend-shared'
import { AlignWrapper } from 'views/components/AlignWrapper/AlignWrapper'

const SeedCropSortableHeadCell = AdvancedHeadCell<keyof SeedCrop>()

const NameCell: React.FC<CellProps<SeedCrop>> = ({ cell, column }) => {
  return <div key={column.id}>{cell.value}</div>
}

const SeedCropList: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation(['seedCrop', 'labels'])
  const [progress, data] = useSeedCropList()
  const filterUpdated = useAction(SeedCropActions.filterUpdated)
  const sortingUpdated = useAction(SeedCropActions.sortingUpdated)
  const listRequested = useAction(SeedCropActions.listRequested)
  const filterValue = useSelector(SeedCropSelectors.filter)
  const total = useSelector(SeedCropSelectors.total)
  const pages = useSelector(SeedCropSelectors.pages)
  const page = useSelector(SeedCropSelectors.page)
  const pageSize = useSelector(SeedCropSelectors.pageSize)
  const { sort_field, sort_reversed } = useSelector(SeedCropSelectors.sorting)

  const visibleColumns = React.useMemo(
    () => [
      {
        Header: t('list.tableHeaders.name'),
        accessor: 'name' as const,
        Cell: NameCell,
        sortable: true,
      },
    ],
    [t],
  )

  const { columns, rows, prepareRow } = useTable<SeedCrop>({
    columns: visibleColumns,
    data: data as SeedCrop[],
  })

  const handleClearFilters = useCallback(() => {
    filterUpdated({})
  }, [filterUpdated])

  const setPage = useCallback(
    (num) => {
      listRequested({ page: num })
    },
    [listRequested],
  )

  const { scrollRef } = usePersistentScroll('seedCropList')
  const [progressCSV, downloadCSV] = useDownload(endpoints.seedCrop('download/csv'), {
    ...filterValue,
    sort_field,
    sort_reversed,
  })

  const isFilterApplied = Object.values(filterValue).some(Boolean)
  return (
    <TComponents.Wrapper>
      <TComponents.Filters>
        <TComponents.FiltersWrapper></TComponents.FiltersWrapper>
        <AlignWrapper verticalAlign="bottom" horizontalAlign="right">
          <Button onClick={downloadCSV} intent="primary" filled progress={progressCSV}>
            {t('labels:downloadCsv')}
          </Button>
        </AlignWrapper>
      </TComponents.Filters>
      <Table total={total} pages={pages} pageSize={pageSize} currentPage={page} onSetPage={setPage} ref={scrollRef}>
        <TableHead>
          <TableHeadRow>
            {columns.map((column) => {
              return (
                <SeedCropSortableHeadCell
                  key={column.getHeaderProps().key}
                  id={column.id as keyof SeedCrop}
                  sortable={column.sortable}
                  hidden={column.hidden}
                  sortField={sort_field}
                  sortDesc={sort_reversed}
                  onChange={sortingUpdated}
                >
                  {column.render('Header')}
                </SeedCropSortableHeadCell>
              )
            })}
          </TableHeadRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            prepareRow(row)
            const { key, ...props } = row.getRowProps()
            return (
              <TableBodyRow
                key={key}
                {...props}
                onClick={() => {
                  history.push(generatePath(Routes.Edit, { id: row.original.id.toString() }))
                }}
              >
                {row.cells.map((cell) => {
                  const { key, ...props } = cell.getCellProps()
                  return (
                    <TableBodyCell key={key} {...props}>
                      {cell.render('Cell')}
                    </TableBodyCell>
                  )
                })}
              </TableBodyRow>
            )
          })}
          <TableNoData
            progress={progress}
            isEmpty={!rows.length}
            colSpan={visibleColumns.length}
            loading={<TComponents.Spinner />}
          >
            <div>{isFilterApplied ? t('list.emptyFilterMsg') : t('list.emptyMsg')}</div>
            {isFilterApplied && progress !== Progress.WORK && (
              <TComponents.ClearButton intent={'cancel'} size={'small'} onClick={handleClearFilters}>
                {t('list.resetAllFilters')}
              </TComponents.ClearButton>
            )}
          </TableNoData>
        </TableBody>
      </Table>
    </TComponents.Wrapper>
  )
}

export default SeedCropList
